import { useEffect, useRef, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/* The redesign's side panel: a card fixed at the right, inset from the window's edges, opened over a
 * list rather than in place of it.
 *
 * **Not a modal, and that is the difference from `Drawer`.** The list behind stays live, so choosing
 * another row moves the panel to that row — which is how the design reads a queue: open one, glance,
 * open the next. `Drawer` is for a task that should hold the page (a form with a scrim and a focus
 * trap); this is for reading one record beside the others.
 *
 * Focus moves to the close control when the panel opens or changes subject, so a keyboard reader lands
 * inside it, and Escape closes it.
 */
export function SidePanel({
  label,
  onClose,
  head,
  children,
  className,
}: {
  /** The panel's accessible name — the record it shows. */
  readonly label: string;
  readonly onClose: () => void;
  /** What sits beside the close control: a pill, the record's name, a line under it. */
  readonly head: ReactNode;
  readonly children: ReactNode;
  /** A modifier for a panel drawn at a different width, such as the 500px breaks drawer. */
  readonly className?: string | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, [label]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <aside
      className={className === undefined ? 'lq-side' : `lq-side ${className}`}
      aria-label={label}
    >
      <div className="lq-side__head">
        <div className="lq-side__title">{head}</div>
        <button
          ref={closeRef}
          type="button"
          className="lq-side__close"
          aria-label={t('common.close')}
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <div className="lq-side__body">{children}</div>
    </aside>
  );
}
